/**
 * PlatformRegistry — Single source of truth for all platform configurations.
 * Centralizes skill IDs, naming conventions, categories, types and variables
 * that were previously hardcoded across ThanksOrderTypesConfig, VariablesConfig, etc.
 *
 * @module PlatformRegistry
 */

// ---------------------------------------------------------------------------
// Platform Definitions
// ---------------------------------------------------------------------------

/** @enum {string} */
export const PLATFORM_CATEGORIES = {
    ECOMMERCE: 'ecommerce',
    BOOKING: 'booking',
};

/**
 * @typedef {Object} Platform
 * @property {string} key      - Internal key (e.g. 'shopify', 'restoo')
 * @property {string} id       - Skill UUID
 * @property {string} name     - Human-readable name
 * @property {string} category - 'ecommerce' | 'booking'
 * @property {string|null} prefix - Template name prefix (null = legacy ecommerce naming)
 * @property {boolean} [freeMode] - Plantillas de formato libre: además de la lista de
 *   variables de la plataforma se pueden escribir variables propias ({{mi_variable}}) y
 *   configurar botones, igual que en las cuentas sin plataforma reconocida.
 * @property {boolean} [integrationDocs] - La plantilla la dispara un sistema externo por
 *   webhook: muestra el botón «<>» de instrucciones de integración en la tabla.
 */

/** @type {Object<string, Platform>} */
export const PLATFORMS = {
    // --- ECOMMERCE (mutuamente excluyentes) ---
    shopify: {
        key: 'shopify',
        id: 'd74f8439-bda9-420a-8b20-cbb80e5f7510',
        name: 'Shopify',
        category: PLATFORM_CATEGORIES.ECOMMERCE,
        prefix: null,
    },
    woocommerce: {
        key: 'woocommerce',
        id: 'ab7aa0d4-9f70-4144-af1e-a926232b4871',
        name: 'WooCommerce',
        category: PLATFORM_CATEGORIES.ECOMMERCE,
        prefix: null,
    },
    prestashop: {
        key: 'prestashop',
        id: '124f8439-bca9-420a-8f20-c1b80e5f7512',
        name: 'PrestaShop',
        category: PLATFORM_CATEGORIES.ECOMMERCE,
        prefix: null,
    },
    magento: {
        key: 'magento',
        id: '742de54f-6ed3-4357-86dd-5be8e329c0f8',
        name: 'Magento',
        category: PLATFORM_CATEGORIES.ECOMMERCE,
        prefix: null,
    },
    tiendanube: {
        key: 'tiendanube',
        id: '7d2f4c8a-91b3-4e6d-a5f0-3c8e2b7d9a14',
        name: 'Tiendanube',
        category: PLATFORM_CATEGORIES.ECOMMERCE,
        prefix: null,
    },

    // --- BOOKING (coexisten entre sí y con ecommerce) ---
    restoo: {
        key: 'restoo',
        id: 'e7c90232-e631-4577-98cb-1b76c2246636',
        name: 'Restoo',
        category: PLATFORM_CATEGORIES.BOOKING,
        prefix: 'restoo',
    },
    wineobs: {
        key: 'wineobs',
        id: '68ed0420-7bde-46a7-b473-6246f83e79a9',
        name: 'Wineobs',
        category: PLATFORM_CATEGORIES.BOOKING,
        prefix: 'wineobs',
    },
    gcalendar: {
        key: 'gcalendar',
        id: 'f006faf0-9174-4c64-90a9-d01c6c24dbeb',
        name: 'Google Calendar',
        category: PLATFORM_CATEGORIES.BOOKING,
        prefix: 'gcalendar',
    },
    turnochic: {
        key: 'turnochic',
        id: '9a1b2c3d-4e5f-6789-abcd-ef0123456789',
        name: 'TurnoChic',
        category: PLATFORM_CATEGORIES.BOOKING,
        prefix: 'turnochic',
        freeMode: true,
        integrationDocs: true,
    },
};

// ---------------------------------------------------------------------------
// Template Types per Platform
// ---------------------------------------------------------------------------

/** @type {Object<string, Array<{value: string, i18nKey: string}>>} */
export const PLATFORM_TYPES = {
    ecommerce: [
        { value: 'default', i18nKey: 'thanksOrder.types.default' },
        { value: 'pickup', i18nKey: 'thanksOrder.types.pickup' },
        { value: 'cod', i18nKey: 'thanksOrder.types.cod' },
        { value: 'bank', i18nKey: 'thanksOrder.types.bank' },
    ],
    restoo: [
        { value: 'confirmed', i18nKey: 'thanksOrder.types.confirmed' },
        { value: 'reminder', i18nKey: 'thanksOrder.types.reminder' },
        { value: 'cancelled', i18nKey: 'thanksOrder.types.cancelled' },
        { value: 'modified', i18nKey: 'thanksOrder.types.modified' },
        { value: 'no_show', i18nKey: 'thanksOrder.types.noShow' },
    ],
    wineobs: [
        { value: 'confirmed', i18nKey: 'thanksOrder.types.confirmed' },
        { value: 'cancelled', i18nKey: 'thanksOrder.types.cancelled' },
    ],
    gcalendar: [
        { value: 'confirmed', i18nKey: 'thanksOrder.types.confirmed' },
        { value: 'cancelled', i18nKey: 'thanksOrder.types.cancelled' },
    ],
    turnochic: [
        { value: 'confirmed', i18nKey: 'thanksOrder.types.confirmed' },
        { value: 'reminder', i18nKey: 'thanksOrder.types.reminder' },
        { value: 'cancelled', i18nKey: 'thanksOrder.types.cancelled' },
    ],
};

// ---------------------------------------------------------------------------
// Variables per Platform
// ---------------------------------------------------------------------------

/** @type {Array<{label: string, i18nKey: string, value: string, example: string}>} */
export const ECOMMERCE_VARIABLES = [
    {
        label: 'customer_name',
        i18nKey: 'thanksOrder.variables.customerName',
        value: '{{customer_name}}',
        example: 'Susana García',
    },
    {
        label: 'customer_first_name',
        i18nKey: 'thanksOrder.variables.customerFirstName',
        value: '{{customer_first_name}}',
        example: 'Susana',
    },
    {
        label: 'customer_last_name',
        i18nKey: 'thanksOrder.variables.customerLastName',
        value: '{{customer_last_name}}',
        example: 'García',
    },
    {
        label: 'customer_email',
        i18nKey: 'thanksOrder.variables.customerEmail',
        value: '{{customer_email}}',
        example: 'susana@email.com',
    },
    { label: 'agent_name', i18nKey: 'thanksOrder.variables.agentName', value: '{{agent_name}}', example: 'Lixsa' },
    { label: 'name', i18nKey: 'thanksOrder.variables.name', value: '{{name}}', example: 'Susana' },
    {
        label: 'business_name',
        i18nKey: 'thanksOrder.variables.businessName',
        value: '{{business_name}}',
        example: 'Mi Tienda Online',
    },
    {
        label: 'order_number',
        i18nKey: 'thanksOrder.variables.orderNumber',
        value: '{{order_number}}',
        example: '#10432',
    },
    {
        label: 'products',
        i18nKey: 'thanksOrder.variables.products',
        value: '{{products}}',
        example: 'Camiseta Premium, Zapatillas Running',
    },
    { label: 'total_paid', i18nKey: 'thanksOrder.variables.totalPaid', value: '{{total_paid}}', example: '€149.99' },
    { label: 'currency', i18nKey: 'thanksOrder.variables.currency', value: '{{currency}}', example: 'EUR' },
    {
        label: 'order_status_url',
        i18nKey: 'thanksOrder.variables.orderStatusUrl',
        value: '{{order_status_url}}',
        example: 'https://mitienda.com/orders/10432',
    },
    {
        label: 'payment_gateway_names',
        i18nKey: 'thanksOrder.variables.paymentGateway',
        value: '{{payment_gateway_names}}',
        example: 'Stripe',
    },
    {
        label: 'payment_method',
        i18nKey: 'thanksOrder.variables.paymentMethod',
        value: '{{payment_method}}',
        example: 'Visa ****4242',
    },
    {
        label: 'shipping_option',
        i18nKey: 'thanksOrder.variables.shippingOption',
        value: '{{shipping_option}}',
        example: 'Envío Express 24h',
    },
    {
        label: 'shipping_address',
        i18nKey: 'thanksOrder.variables.shippingAddress',
        value: '{{shipping_address}}',
        example: 'Calle Gran Vía 28, Madrid',
    },
    {
        label: 'shipping_price',
        i18nKey: 'thanksOrder.variables.shippingPrice',
        value: '{{shipping_price}}',
        example: '€4.99',
    },
    {
        label: 'pickup_name',
        i18nKey: 'thanksOrder.variables.pickupName',
        value: '{{pickup_name}}',
        example: 'Punto de Recogida Centro',
    },
    {
        label: 'pickup_address',
        i18nKey: 'thanksOrder.variables.pickupAddress',
        value: '{{pickup_address}}',
        example: 'Av. de la Constitución 15',
    },
    {
        label: 'discount_code',
        i18nKey: 'thanksOrder.variables.discountCode',
        value: '{{discount_code}}',
        example: 'VERANO25',
    },
    {
        label: 'discount_amount',
        i18nKey: 'thanksOrder.variables.discountAmount',
        value: '{{discount_amount}}',
        example: '€25.00',
    },
];

/** @type {Array<{label: string, i18nKey: string, value: string, example: string}>} */
export const RESTOO_VARIABLES = [
    {
        label: 'customer_name',
        i18nKey: 'thanksOrder.variables.customerName',
        value: '{{customer_name}}',
        example: 'Susana García',
    },
    {
        label: 'customer_first_name',
        i18nKey: 'thanksOrder.variables.customerFirstName',
        value: '{{customer_first_name}}',
        example: 'Susana',
    },
    { label: 'last_name', i18nKey: 'thanksOrder.variables.lastName', value: '{{last_name}}', example: 'García' },
    { label: 'agent_name', i18nKey: 'thanksOrder.variables.agentName', value: '{{agent_name}}', example: 'Lixsa' },
    {
        label: 'business_name',
        i18nKey: 'thanksOrder.variables.businessName',
        value: '{{business_name}}',
        example: 'Restaurante El Jardín',
    },
    {
        label: 'order_number',
        i18nKey: 'thanksOrder.variables.orderNumber',
        value: '{{order_number}}',
        example: '#R-2048',
    },
    { label: 'guest_count', i18nKey: 'thanksOrder.variables.guestCount', value: '{{guest_count}}', example: '4' },
    {
        label: 'children_count',
        i18nKey: 'thanksOrder.variables.childrenCount',
        value: '{{children_count}}',
        example: '2',
    },
    {
        label: 'booking_date',
        i18nKey: 'thanksOrder.variables.bookingDate',
        value: '{{booking_date}}',
        example: '15 Jun 2026, 20:30',
    },
    {
        label: 'comments',
        i18nKey: 'thanksOrder.variables.comments',
        value: '{{comments}}',
        example: 'Mesa junto a la ventana',
    },
    {
        label: 'products',
        i18nKey: 'thanksOrder.variables.products',
        value: '{{products}}',
        example: 'Mesa Terraza, Menú Degustación',
    },
];

/** @type {Array<{label: string, i18nKey: string, value: string, example: string}>} */
export const WINEOBS_VARIABLES = [
    {
        label: 'customer_name',
        i18nKey: 'thanksOrder.variables.customerName',
        value: '{{customer_name}}',
        example: 'John Smith',
    },
    {
        label: 'customer_first_name',
        i18nKey: 'thanksOrder.variables.customerFirstName',
        value: '{{customer_first_name}}',
        example: 'John',
    },
    { label: 'last_name', i18nKey: 'thanksOrder.variables.lastName', value: '{{last_name}}', example: 'Smith' },
    { label: 'agent_name', i18nKey: 'thanksOrder.variables.agentName', value: '{{agent_name}}', example: 'Lixsa' },
    {
        label: 'business_name',
        i18nKey: 'thanksOrder.variables.businessName',
        value: '{{business_name}}',
        example: 'Bodega López',
    },
    {
        label: 'order_number',
        i18nKey: 'thanksOrder.variables.orderNumber',
        value: '{{order_number}}',
        example: '#W-1024',
    },
    { label: 'guest_count', i18nKey: 'thanksOrder.variables.guestCount', value: '{{guest_count}}', example: '6' },
    {
        label: 'children_count',
        i18nKey: 'thanksOrder.variables.childrenCount',
        value: '{{children_count}}',
        example: '0',
    },
    {
        label: 'booking_date',
        i18nKey: 'thanksOrder.variables.bookingDate',
        value: '{{booking_date}}',
        example: '15 Jun 2026, 11:00',
    },
    {
        label: 'tour_name',
        i18nKey: 'thanksOrder.variables.tourName',
        value: '{{tour_name}}',
        example: 'Sunset Tasting',
    },
    {
        label: 'tour_duration',
        i18nKey: 'thanksOrder.variables.tourDuration',
        value: '{{tour_duration}}',
        example: '2h 30min',
    },
    { label: 'tour_price', i18nKey: 'thanksOrder.variables.tourPrice', value: '{{tour_price}}', example: '$15,000' },
    {
        label: 'products',
        i18nKey: 'thanksOrder.variables.products',
        value: '{{products}}',
        example: 'Degustación Premium',
    },
];

/** @type {Array<{label: string, i18nKey: string, value: string, example: string}>} */
export const GCALENDAR_VARIABLES = [
    {
        label: 'customer_name',
        i18nKey: 'thanksOrder.variables.customerName',
        value: '{{customer_name}}',
        example: 'María López',
    },
    {
        label: 'customer_first_name',
        i18nKey: 'thanksOrder.variables.customerFirstName',
        value: '{{customer_first_name}}',
        example: 'María',
    },
    { label: 'agent_name', i18nKey: 'thanksOrder.variables.agentName', value: '{{agent_name}}', example: 'Lixsa' },
    {
        label: 'business_name',
        i18nKey: 'thanksOrder.variables.businessName',
        value: '{{business_name}}',
        example: 'Clínica Dental Sol',
    },
    {
        label: 'order_number',
        i18nKey: 'thanksOrder.variables.orderNumber',
        value: '{{order_number}}',
        example: '#M-512',
    },
    {
        label: 'meeting_name',
        i18nKey: 'thanksOrder.variables.meetingName',
        value: '{{meeting_name}}',
        example: 'Consulta Inicial',
    },
    {
        label: 'meeting_date',
        i18nKey: 'thanksOrder.variables.meetingDate',
        value: '{{meeting_date}}',
        example: '15 Jun 2026, 10:00',
    },
    {
        label: 'meeting_duration',
        i18nKey: 'thanksOrder.variables.meetingDuration',
        value: '{{meeting_duration}}',
        example: '30 min',
    },
    {
        label: 'meeting_url',
        i18nKey: 'thanksOrder.variables.meetingUrl',
        value: '{{meeting_url}}',
        example: 'https://calendar.google.com/...',
    },
];

/** @type {Array<{label: string, i18nKey: string, value: string, example: string}>} */
export const TURNOCHIC_VARIABLES = [
    {
        label: 'customer_name',
        i18nKey: 'thanksOrder.variables.customerName',
        value: '{{customer_name}}',
        example: 'María García',
    },
    {
        label: 'customer_first_name',
        i18nKey: 'thanksOrder.variables.customerFirstName',
        value: '{{customer_first_name}}',
        example: 'María',
    },
    { label: 'agent_name', i18nKey: 'thanksOrder.variables.agentName', value: '{{agent_name}}', example: 'Lixsa' },
    {
        label: 'business_name',
        i18nKey: 'thanksOrder.variables.businessName',
        value: '{{business_name}}',
        example: 'Clínica Estética Sol',
    },
    {
        label: 'order_number',
        i18nKey: 'thanksOrder.variables.orderNumber',
        value: '{{order_number}}',
        example: '#TC-1024',
    },
    {
        label: 'booking_date',
        i18nKey: 'thanksOrder.variables.bookingDate',
        value: '{{booking_date}}',
        example: '15 Jun 2026, 10:00',
    },
    {
        label: 'service_name',
        i18nKey: 'thanksOrder.variables.serviceName',
        value: '{{service_name}}',
        example: 'Limpieza Facial',
    },
    {
        label: 'service_duration',
        i18nKey: 'thanksOrder.variables.serviceDuration',
        value: '{{service_duration}}',
        example: '45 min',
    },
    {
        label: 'service_price',
        i18nKey: 'thanksOrder.variables.servicePrice',
        value: '{{service_price}}',
        example: '€65.00',
    },
    {
        label: 'staff_name',
        i18nKey: 'thanksOrder.variables.staffName',
        value: '{{staff_name}}',
        example: 'Dra. Laura Martínez',
    },
    {
        label: 'location_name',
        i18nKey: 'thanksOrder.variables.locationName',
        value: '{{location_name}}',
        example: 'Sede Central',
    },
    {
        label: 'booking_day',
        i18nKey: 'thanksOrder.variables.bookingDay',
        value: '{{booking_day}}',
        example: '15 de junio de 2026',
    },
    {
        label: 'booking_time',
        i18nKey: 'thanksOrder.variables.bookingTime',
        value: '{{booking_time}}',
        example: '10:00',
    },
    {
        label: 'booking_end_time',
        i18nKey: 'thanksOrder.variables.bookingEndTime',
        value: '{{booking_end_time}}',
        example: '10:45',
    },
    {
        label: 'booking_status',
        i18nKey: 'thanksOrder.variables.bookingStatus',
        value: '{{booking_status}}',
        example: 'Confirmada',
    },
    {
        label: 'customer_phone',
        i18nKey: 'thanksOrder.variables.customerPhone',
        value: '{{customer_phone}}',
        example: '+34 612 345 678',
    },
    {
        label: 'customer_email',
        i18nKey: 'thanksOrder.variables.customerEmail',
        value: '{{customer_email}}',
        example: 'maria@email.com',
    },
    {
        label: 'location_address',
        i18nKey: 'thanksOrder.variables.locationAddress',
        value: '{{location_address}}',
        example: 'Calle Gran Vía 28, Madrid',
    },
    {
        label: 'location_phone',
        i18nKey: 'thanksOrder.variables.locationPhone',
        value: '{{location_phone}}',
        example: '+34 910 000 000',
    },
    {
        label: 'service_address',
        i18nKey: 'thanksOrder.variables.serviceAddress',
        value: '{{service_address}}',
        example: 'Av. de la Constitución 15, 2º B',
    },
];

/** @type {Object<string, Array<{label: string, i18nKey: string, value: string, example: string}>>} */
export const PLATFORM_VARIABLES = {
    ecommerce: ECOMMERCE_VARIABLES,
    restoo: RESTOO_VARIABLES,
    wineobs: WINEOBS_VARIABLES,
    gcalendar: GCALENDAR_VARIABLES,
    turnochic: TURNOCHIC_VARIABLES,
};

// ---------------------------------------------------------------------------
// Generic (no main app) — Variables & Types
// ---------------------------------------------------------------------------

/** @type {Array<{label: string, i18nKey: string, value: string, example: string}>} */
export const GENERIC_VARIABLES = [
    {
        label: 'customer_name',
        i18nKey: 'thanksOrder.variables.customerName',
        value: '{{customer_name}}',
        example: 'Susana García',
    },
    {
        label: 'customer_first_name',
        i18nKey: 'thanksOrder.variables.customerFirstName',
        value: '{{customer_first_name}}',
        example: 'Susana',
    },
    { label: 'agent_name', i18nKey: 'thanksOrder.variables.agentName', value: '{{agent_name}}', example: 'Lixsa' },
    {
        label: 'business_name',
        i18nKey: 'thanksOrder.variables.businessName',
        value: '{{business_name}}',
        example: 'Mi Negocio',
    },
    {
        label: 'order_number',
        i18nKey: 'thanksOrder.variables.orderNumber',
        value: '{{order_number}}',
        example: '#10432',
    },
    {
        label: 'products',
        i18nKey: 'thanksOrder.variables.products',
        value: '{{products}}',
        example: 'Producto A, Producto B',
    },
    { label: 'total_paid', i18nKey: 'thanksOrder.variables.totalPaid', value: '{{total_paid}}', example: '€149.99' },
];

/** @type {Array<{value: string, i18nKey: string}>} */
export const GENERIC_TYPES = [{ value: 'default', i18nKey: 'thanksOrder.types.default' }];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Finds a platform by its skill UUID.
 * @param {string} skillId - Skill UUID
 * @returns {Platform|null}
 */
export function getPlatformBySkillId(skillId) {
    if (!skillId) return null;
    return Object.values(PLATFORMS).find((p) => p.id === skillId) || null;
}

/**
 * Returns the list of platforms the user has active.
 * @param {string[]} activeSkillIds - Array of active skill UUIDs from agent_skills
 * @returns {Platform[]}
 */
export function getActivePlatforms(activeSkillIds) {
    if (!activeSkillIds?.length) return [];
    return Object.values(PLATFORMS).filter((p) => activeSkillIds.includes(p.id));
}

/**
 * @param {Platform} platform
 * @returns {boolean}
 */
export function isEcommercePlatform(platform) {
    return platform?.category === PLATFORM_CATEGORIES.ECOMMERCE;
}

/**
 * @param {Platform} platform
 * @returns {boolean}
 */
export function isBookingPlatform(platform) {
    return platform?.category === PLATFORM_CATEGORIES.BOOKING;
}

/**
 * Returns the template types available for a given platform.
 * @param {Platform|null} platform
 * @returns {Array<{value: string, i18nKey: string}>}
 */
export function getTypesByPlatform(platform) {
    if (!platform) return GENERIC_TYPES;
    return PLATFORM_TYPES[platform.key] || PLATFORM_TYPES[platform.category] || PLATFORM_TYPES.ecommerce;
}

/**
 * Returns the template variables available for a given platform.
 * @param {Platform|null} platform
 * @returns {Array<{label: string, value: string, example: string}>}
 */
export function getVariablesByPlatform(platform) {
    if (!platform) return GENERIC_VARIABLES;
    return PLATFORM_VARIABLES[platform.key] || PLATFORM_VARIABLES[platform.category] || PLATFORM_VARIABLES.ecommerce;
}

/**
 * True when the platform accepts free-form templates: the variables list is a helper,
 * not a closed set, so the account can also type its own {{variables}} and add buttons.
 * @param {Platform|null} platform
 * @returns {boolean}
 */
export function isFreeModePlatform(platform) {
    return platform?.freeMode === true;
}

/**
 * Collects ALL valid type values across all platforms (for parsing).
 * @returns {string[]}
 */
export function getAllValidTypeValues() {
    const types = new Set();
    Object.values(PLATFORM_TYPES).forEach((arr) => arr.forEach((t) => types.add(t.value)));
    return [...types];
}

// ---------------------------------------------------------------------------
// Template Naming
// ---------------------------------------------------------------------------

/**
 * Sanitizes a custom name for embedding in a template name.
 * Removes accents, special chars, normalizes underscores.
 * @param {string} name
 * @returns {string}
 */
function sanitizeName(name) {
    return name
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '')
        .substring(0, 40);
}

/**
 * Finds the last index of an element matching a predicate in an array.
 * @param {Array} arr
 * @param {Function} predicate
 * @returns {number}
 */
function findLastIndex(arr, predicate) {
    for (let i = arr.length - 1; i >= 0; i--) {
        if (predicate(arr[i], i, arr)) return i;
    }
    return -1;
}

/**
 * Builds a template name following the convention:
 * - Without custom name: {prefix}_{type}_{lang}
 * - With custom name:    {prefix}_{customName}_{type}_{lang}
 * - Ecommerce prefix is 'thanks_order'
 *
 * Modified by Martin - 2026-07-02 — Accept optional customName
 *
 * @param {Platform|null} platform
 * @param {string} type
 * @param {string} language
 * @param {string} [customName] - Optional custom display name to embed
 * @returns {string}
 */
export function buildTemplateName(platform, type, language, customName) {
    const lang = (language || 'en').toLowerCase();
    const sanitized = customName ? sanitizeName(customName) : null;
    const prefix = platform && platform.prefix ? platform.prefix : 'thanks_order';
    if (sanitized) {
        return `${prefix}_${sanitized}_${type}_${lang}`;
    }
    return `${prefix}_${type}_${lang}`;
}

/**
 * Parses a template name to extract its platform and type.
 *
 * @param {string} templateName
 * @param {Platform[]} [activePlatforms] - Optional. When provided, resolves ecommerce platform from active list.
 * @returns {{ platform: Platform|null, type: string, isLegacy: boolean }}
 */
export function parseTemplateName(templateName, activePlatforms) {
    if (!templateName) return { platform: null, type: 'default', isLegacy: true };

    const allValidTypes = getAllValidTypeValues();

    // Convention: {prefix}_{customName?}_{type}_{lang}

    // 1. Check for booking platform prefix (wineobs_, gcalendar_, restoo_)
    const bookingPlatforms = Object.values(PLATFORMS).filter((p) => p.prefix);
    for (const platform of bookingPlatforms) {
        if (templateName.startsWith(`${platform.prefix}_`)) {
            const rest = templateName.slice(platform.prefix.length + 1);
            return parseRestSegments(rest, platform, allValidTypes);
        }
    }

    // 2. Ecommerce format: thanks_order_{customName?}_{type}_{lang}
    if (templateName.startsWith('thanks_order_')) {
        const rest = templateName.slice('thanks_order_'.length);
        const ecommPlatform = resolveEcommercePlatform(activePlatforms);
        return parseRestSegments(rest, ecommPlatform, allValidTypes);
    }

    // 3. Unknown prefix — try stripping language suffixes to extract type
    const stripped = templateName.replace(/(_[a-z]{2}(_[a-z]{2})?)+$/i, '');
    if (stripped && stripped !== templateName) {
        return { platform: null, type: stripped, isLegacy: true };
    }

    // 4. Unknown format
    return { platform: null, type: 'default', isLegacy: true };
}

/**
 * Parses the rest of a template name (after prefix) to extract type and custom name.
 * Convention: {customName?}_{type}_{lang}
 * Parses type from the END — the last segment matching a known type value.
 *
 * @param {string} rest - Everything after the prefix (e.g., 'confirmacion_reserva_confirmed_es')
 * @param {Platform|null} platform
 * @param {string[]} allValidTypes
 * @returns {{ platform: Platform|null, type: string, isLegacy: boolean, customDisplayName?: string }}
 */
function parseRestSegments(rest, platform, allValidTypes) {
    // Strip trailing language suffixes (_xx or _xx_xx)
    const withoutLang = rest.replace(/(_[a-z]{2}(_[a-z]{2})?)+$/i, '') || '';
    if (!withoutLang) {
        return { platform, type: 'default', isLegacy: false };
    }

    const segments = withoutLang.split('_');

    // Find the LAST segment that is a known type
    const lastTypeIdx = findLastIndex(segments, (s) => allValidTypes.includes(s));

    if (lastTypeIdx >= 0) {
        const type = segments[lastTypeIdx];
        const customParts = segments.slice(0, lastTypeIdx);
        const customDisplayName =
            customParts.length > 0 ? customParts.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : null;
        return { platform, type, isLegacy: false, customDisplayName };
    }

    // No known type found — entire rest is custom name, default type
    const customDisplayName = segments.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    return { platform, type: 'default', isLegacy: false, customDisplayName };
}

/**
 * Resolves the active ecommerce platform from a list of active platforms.
 * Since only one ecommerce platform can be active at a time, returns the first match.
 *
 * @param {Platform[]} [activePlatforms]
 * @returns {Platform|null}
 */
export function resolveEcommercePlatform(activePlatforms) {
    if (!activePlatforms?.length) return null;
    return activePlatforms.find((p) => p.category === PLATFORM_CATEGORIES.ECOMMERCE) || null;
}
